import type { AdminCard, AdminGiftCase } from "../types";

/**
 * The gift detail view carries five vocabularies at once — the Neon gift state, the
 * recipient binding, the provenance verdict, the durable operation state, and a
 * provider movement state — and none of the raw tokens says what it means.
 *
 * `adopted` with an empty recipient customer and an empty movement list reads as
 * three separate failures. It is one ordinary record: a recipient has taken the card,
 * the banking customer is not resolved until redemption, and a gift funded before the
 * movement ledger shipped has no legs to show. Spelling that out here is the whole
 * point of the module — it is the portal's single copy of the gift vocabulary, the
 * role `kycOps.ts` plays for verification states.
 */
export type GiftTone = "good" | "neutral" | "warn" | "danger";

export interface ValueGuide {
  /** Plain-English replacement for the raw token. */
  label: string;
  tone: GiftTone;
  /** What the record asserts. */
  summary: string;
  /** What follows from it: where the money sits, or what an operator should do. */
  detail: string;
}

function normalise(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

const UNKNOWN_GIFT_STATE: ValueGuide = {
  label: "Unmapped state",
  tone: "neutral",
  summary: "The API returned a gift state this portal does not recognise.",
  detail: "Trust the raw token over this screen and escalate. An unmapped state normally means the gift lifecycle gained one and the portal has not caught up."
};

/**
 * Ordered as the lifecycle runs. `adopted` and `claimed` are the pair that causes the
 * most confusion: adoption is an identity event, redemption is the money event.
 */
const GIFT_STATES: Readonly<Record<string, ValueGuide>> = {
  creating: {
    label: "Being created",
    tone: "neutral",
    summary: "The gift row exists but no funding transfer is attached to it yet.",
    detail: "A bank deposit or escrow transfer may be processing. The card has not officially sent; inspect funding progress and provider movements."
  },
  pending: {
    label: "Funds staged",
    tone: "neutral",
    summary: "The sender's funds are reserved in gift escrow and nobody has taken the gift.",
    detail: "This is the state the API's attention rules are written against. Legacy binding and provenance problems only raise a flag while a gift sits here."
  },
  awaiting_claim: {
    label: "Awaiting claim",
    tone: "neutral",
    summary: "Funding succeeded and the card was delivered. No recipient has accepted it.",
    detail: "The funds stay in escrow until a recipient adopts and then redeems, or the gift is reversed."
  },
  adopted: {
    label: "Adopted by recipient",
    tone: "neutral",
    summary: "A recipient proved they were an intended recipient and attached this gift to their account.",
    detail: "Adoption is an identity event, not a money event. Adoption does not prove funding. Check the escrow movement; no release leg exists until the recipient redeems."
  },
  claimed: {
    label: "Redeemed",
    tone: "good",
    summary: "The escrowed funds were released into the recipient's Ottagive Cash balance.",
    detail: "This is the only state that records a recipient customer GUID. A redeemed gift is terminal."
  },
  reversed: {
    label: "Returned to sender",
    tone: "neutral",
    summary: "The reserved funds went back to the sender's Ottagive Cash balance.",
    detail: "Terminal. Refund legs carry the `gift_refund` and `service_fee_refund` purposes."
  },
  failed: {
    label: "Not sent",
    tone: "danger",
    summary: "The send failed. The recipient cannot redeem this gift.",
    detail: "Some money may have moved and been refunded. Check the original and reversing legs. Successful bank deposits remain in the sender’s Cash balance."
  },
  refund_pending: {
    label: "Refund pending", tone: "warn", summary: "The card is withdrawn while payment recovery completes.",
    detail: "Do not describe the payment as refunded until all reversing legs complete."
  },
  requires_review: {
    label: "Payment needs review", tone: "danger", summary: "Funding or recovery has an unresolved outcome.",
    detail: "Check the operation and provider movements before retrying. The gift cannot be redeemed in this state."
  },
  legacy_unbound: {
    label: "Legacy — no gift record",
    tone: "danger",
    summary: "A staged `giftCardSent` transfer exists with no `gifts` row behind it.",
    detail: "This gift predates authoritative recipient binding. It cannot be safely released and needs the reviewed remediation path."
  }
};

export const FUNDED_GIFT_STATES = ["pending", "awaiting_claim", "adopted", "claimed", "reversed", "legacy_unbound"] as const;

export function giftWasFunded(state: string): boolean {
  return (FUNDED_GIFT_STATES as readonly string[]).includes(normalise(state));
}

export function giftStateGuide(state: string): ValueGuide {
  return GIFT_STATES[normalise(state)] ?? UNKNOWN_GIFT_STATE;
}

const BINDINGS: Readonly<Record<AdminGiftCase["bindingStatus"], ValueGuide>> = {
  verified: {
    label: "Verified",
    tone: "good",
    summary: "Neon holds at least one server-side recipient claim for this gift.",
    detail: "Who may redeem it was decided by the API, not by client-writable card content. Note that adoption itself writes a claim, so a legacy gift flips to verified the moment a recipient takes it — verified does not prove the gift was bound before it was funded."
  },
  legacy_unbound: {
    label: "Legacy unbound",
    tone: "danger",
    summary: "No server-side recipient claim exists, so nothing authorises a payout.",
    detail: "Do not release these funds. A recipient hint, a card, or a contact method is evidence, never authority."
  }
};

export function bindingGuide(status: AdminGiftCase["bindingStatus"]): ValueGuide {
  return BINDINGS[status] ?? UNKNOWN_GIFT_STATE;
}

const PROVENANCE: Readonly<Record<AdminGiftCase["provenanceStatus"], ValueGuide>> = {
  complete: {
    label: "Complete",
    tone: "good",
    summary: "The stored funding legs reconcile to the gift amount and the commission charged.",
    detail: "The movement list below is the full, immutable account of what moved."
  },
  partial: {
    label: "Partial",
    tone: "warn",
    summary: "Funding legs exist but they do not add up to the gift amount and fee.",
    detail: "Compare each leg against Cybrid and the reconciliation job. Do not fill the gap from balances or card copy."
  },
  unavailable: {
    label: "Unavailable",
    tone: "warn",
    summary: "No `gift_value` or `service_fee` leg is stored for this gift at all.",
    detail: "Expected for a gift funded before the movement ledger existed, and not a fault on its own. The history genuinely cannot be reconstructed — recording it from current balances or card content would be a fabrication."
  }
};

export function provenanceGuide(status: AdminGiftCase["provenanceStatus"]): ValueGuide {
  return PROVENANCE[status] ?? UNKNOWN_GIFT_STATE;
}

const OPERATION_STATES: Readonly<Record<string, ValueGuide>> = {
  accepted: {
    label: "Accepted",
    tone: "neutral",
    summary: "The durable command was recorded and handed to a worker.",
    detail: "Nonterminal. Past fifteen minutes the API raises a stalled-operation flag."
  },
  processing: {
    label: "Processing",
    tone: "neutral",
    summary: "A worker holds the lease and the command is retryable.",
    detail: "Nonterminal. Let reconciliation converge it; the portal has no retry because the API exposes no staff-authorised one."
  },
  submitting: {
    label: "Submitting",
    tone: "neutral",
    summary: "The command is mid-flight to the provider.",
    detail: "Nonterminal. Treat a long stay here as a delivery or worker problem, not a customer one."
  },
  succeeded: {
    label: "Succeeded",
    tone: "good",
    summary: "The command completed and its result was written.",
    detail: "Terminal. The gift state and movement legs are the record of what it did."
  },
  failed: {
    label: "Failed",
    tone: "danger",
    summary: "The command stopped on an error the worker judged non-retryable.",
    detail: "Read the error code with the money movements before assuming nothing moved."
  },
  requires_review: {
    label: "Requires review",
    tone: "danger",
    summary: "The worker could not establish whether the provider side completed.",
    detail: "Ambiguous, not failed. Confirm against Cybrid before any remediation is even proposed."
  }
};

export function operationStateGuide(state: string): ValueGuide {
  return OPERATION_STATES[normalise(state)] ?? UNKNOWN_GIFT_STATE;
}

const OPERATION_TYPES: Readonly<Record<string, string>> = {
  send: "Send",
  claim: "Claim (adoption)",
  redeem: "Redeem",
  reverse: "Reverse"
};

export function operationTypeLabel(type: string): string {
  return OPERATION_TYPES[normalise(type)] ?? type;
}

const MOVEMENT_STATES: Readonly<Record<string, ValueGuide>> = {
  planned: {
    label: "Planned",
    tone: "neutral",
    summary: "The leg was recorded before the provider was called.",
    detail: "A leg stuck at planned means the provider call never landed. Nothing moved."
  },
  pending: {
    label: "Pending",
    tone: "neutral",
    summary: "The provider accepted the transfer and has not settled it.",
    detail: "Nonterminal. Past fifteen minutes the API raises a stale-movement flag."
  },
  submitting: {
    label: "Submitting",
    tone: "neutral",
    summary: "The leg is mid-flight to the provider.",
    detail: "Nonterminal, and counted by the stale-movement rule."
  },
  completed: {
    label: "Completed",
    tone: "good",
    summary: "The provider settled this leg.",
    detail: "Terminal and immutable. Corrections are recorded as a new reversing leg, never an edit."
  },
  failed: {
    label: "Failed",
    tone: "danger",
    summary: "The provider rejected or cancelled this leg.",
    detail: "Check the provider failure code and confirm the sender was not debited."
  },
  requires_review: {
    label: "Requires review",
    tone: "danger",
    summary: "The API could not tell whether this leg moved money.",
    detail: "Ambiguous, not failed. Reconcile against Cybrid before proposing anything."
  }
};

export function movementStateGuide(state: string): ValueGuide {
  return MOVEMENT_STATES[normalise(state)] ?? UNKNOWN_GIFT_STATE;
}

const MOVEMENT_PURPOSES: Readonly<Record<string, string>> = {
  bank_funding: "Bank funding",
  bank_funding_recovery: "Instant funding recovery",
  gift_value: "Gift value into escrow",
  service_fee: "Ottagive service fee",
  gift_release: "Release to recipient",
  gift_refund: "Refund to sender",
  service_fee_refund: "Service fee refund"
};

export function movementPurposeLabel(purpose: string): string {
  return MOVEMENT_PURPOSES[normalise(purpose)] ?? purpose;
}

/** The recipient customer field, which is empty for most of a gift's life by design. */
export function recipientGuide(gift: AdminGiftCase): { value: string; guide: ValueGuide } {
  if (gift.recipientCustomerGuid) {
    return {
      value: gift.recipientCustomerGuid,
      guide: {
        label: "Resolved",
        tone: "good",
        summary: "A banking customer is recorded as having received these funds.",
        detail: "This is written at redemption, in the same commit that releases the money."
      }
    };
  }
  const state = normalise(gift.giftState);
  if (state === "claimed") {
    return {
      value: "Missing",
      guide: {
        label: "Missing",
        tone: "danger",
        summary: "The gift is redeemed but no recipient customer was recorded.",
        detail: "Redemption writes the state and the customer together, so this pairing should not occur. Capture it and escalate."
      }
    };
  }
  if (state === "adopted") {
    return {
      value: "Not resolved yet",
      guide: {
        label: "Not resolved yet",
        tone: "neutral",
        summary: "A recipient has adopted this gift but has not redeemed it.",
        detail: "Empty is correct here. A gift is addressed to an identity claim, and the recipient often has no banking customer until they redeem — which is where the API resolves this field."
      }
    };
  }
  return {
    value: "Nobody has taken this gift",
    guide: {
      label: "Unclaimed",
      tone: "neutral",
      summary: "No recipient has adopted or redeemed this gift.",
      detail: "Empty is correct until someone does. The recipient hint shows who it was addressed to; it authorises nothing."
    }
  };
}

/**
 * Cross-field checks the API does not run. Its own attention rules only fire while a
 * gift is `pending`, so a contradiction that appears after adoption — a redeemed gift
 * with no recipient, say — reaches this screen with no flag on it at all.
 */
export function giftContradictions(gift: AdminGiftCase): string[] {
  const found: string[] = [];
  const state = normalise(gift.giftState);
  const settled = state === "claimed" || state === "reversed";
  if (state === "claimed" && !gift.recipientCustomerGuid) {
    found.push("Redeemed, but no recipient customer is recorded. Redemption writes both in one commit.");
  }
  if (gift.recipientCustomerGuid && !settled) {
    found.push("A recipient customer is recorded although the gift is not redeemed or reversed. Only redemption should write that field.");
  }
  if (state === "claimed" && gift.movements.length > 0 && !gift.movements.some((movement) => normalise(movement.purpose) === "gift_release")) {
    found.push("Redeemed with movement legs stored, but none of them releases funds to the recipient.");
  }
  if (gift.provenanceStatus === "complete" && gift.movements.length === 0) {
    found.push("Provenance reads complete with no movement legs stored. One of the two is wrong.");
  }
  return found;
}

/** Whether the API's attention rules were even evaluated against this gift. */
export function attentionRulesApply(gift: AdminGiftCase): boolean {
  return normalise(gift.giftState) === "pending";
}

function dollars(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

/**
 * How the Firestore card projection lines up with the ledger row it belongs to.
 *
 * The projection is client-written, so it is evidence and never authority. It earns a
 * column anyway: a card claiming to be redeemed against a gift the ledger has not
 * released is exactly the discrepancy support is looking for, and nothing else on the
 * screen would surface it.
 */
export function cardDiscrepancies(gift: AdminGiftCase, card: AdminCard | null): string[] {
  if (!card) return [];
  const found: string[] = [];
  const state = normalise(gift.giftState);
  if (card.redeemed && state !== "claimed") {
    found.push("The card is marked redeemed while the ledger has not released the funds. The card is client-written, so trust the ledger and investigate the card.");
  }
  if (card.amountCents !== null && card.amountCents !== gift.amountCents) {
    found.push(`The card shows ${dollars(card.amountCents)} where the ledger holds ${dollars(gift.amountCents)}.`);
  }
  return found;
}

/**
 * What the card cell should say, including why it is empty. A bare em dash here reads
 * as "no card exists", which is wrong in three of the four empty cases.
 */
export function cardProjectionGuide(gift: AdminGiftCase, card: AdminCard | null): ValueGuide {
  if (card) {
    const disagreements = cardDiscrepancies(gift, card);
    if (disagreements.length) {
      return {
        label: "Disagrees with ledger",
        tone: "danger",
        summary: "The card projection and the authoritative gift record do not match.",
        detail: disagreements.join(" ")
      };
    }
    return {
      label: card.redeemed ? "Card marked redeemed" : "Card outstanding",
      tone: "neutral",
      summary: "The legacy card document for this transfer, consistent with the ledger.",
      detail: "Content and delivery evidence only. It never authorizes a recipient or a payout."
    };
  }
  if (gift.firestoreCardId) {
    return {
      label: "V2 card record",
      tone: "neutral",
      summary: "Neon points at a card in the `cards` collection rather than the legacy per-user store.",
      detail: "Gifts sent through the V2 flow write `cards/{giftId}`, so having no legacy `sentCards` document is expected."
    };
  }
  if (!gift.senderFirebaseUid) {
    return {
      label: "No Firebase sender",
      tone: "neutral",
      summary: "The gift records no sender Firebase UID, so no card store can be searched for it.",
      detail: "Common for legacy transfers staged before gifts carried a Firebase identity."
    };
  }
  return {
    label: "No card indexed",
    tone: "neutral",
    summary: "The sender's card store holds no document naming this transfer.",
    detail: "Either the card predates the transfer link, or it lies beyond the per-sender read limit. Absence here is not evidence that the gift was never delivered."
  };
}
